import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { parseISO } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import useCaseTypesList from "../../hooks/useCaseTypesList.js";
import { createCase } from "../../api/cases.js";
import { uploadDocs } from "../../api/documents.js";
import useSnackbar from "../../hooks/useSnackbar.js";
import useErrorHandler from "../../hooks/useErrorHandler.js";

const emptyCaseData = {
  description: "",
  title: "",
  start_date: "",
  end_date: "",
  status: "INITIAL",
  type: "",
  respondent_name: "",
  respondent_email: "",
  respondent_phone: "",
  claim_amount: "",
  arbitration_agreement: false,
};
export default function NewCase() {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const errorHandler = useErrorHandler();

  const { data: user } = useSelector((state) => state.user);

  const [data, setData] = useState(emptyCaseData);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const onInputChange = ({ target }) => {
    setData((prev) => ({ ...prev, [target.name]: target.type === "checkbox" ? target.checked : target.value }));
  };

  const { caseTypeList } = useCaseTypesList();

  const onCaseTypeChange = ({ target }) => {
    setData((prev) => ({ ...prev, [target.name]: target.value }));
  };

  const onCreateFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await createCase(user.token, {
        ...data,
        claim_amount: Number(data.claim_amount),
        // interview: /^admin$/i.test(user.roles) ? [] : questionsDone,
      });
      const caseId = res.data.caseId;

      // Supporting documents go up one by one once the case exists, so a failure can name the file. The case is already
      // created at this point, so a failed upload sends the user to the case page to add the file there.
      const failed = [];
      for (const file of files) {
        try {
          const formData = new FormData();
          formData.append("text", "Supporting document at filing");
          formData.append("users", "");
          formData.append("files", file);
          await uploadDocs(user.token, caseId, formData);
        } catch (uploadErr) {
          failed.push(file.name);
        }
      }
      if (failed.length) {
        snackbar.showError(`Case created, but these files could not be uploaded: ${failed.join(", ")}. You can add them from the case page.`);
        navigate(`/cases/${caseId}`);
        return;
      }
      snackbar.showSuccess(res.data.msg);
      navigate("/cases");
    } catch (err) {
      errorHandler(err);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <>
      {/* <Box mb={2} display="flex" justifyContent="flex-end">
        <Link to="/cases">
          <Button variant="contained" size="small" color="secondary">
            Cancel
          </Button>
        </Link>
      </Box> */}
       <Paper className="odr-card">
        <Box
          display="flex"
          justifyContent="space-between"
          className="odr-card-header"
        >
          <Typography className="heading-title">Case Details</Typography>
        </Box>

        <Box px={2} pb={2} component="form" onSubmit={onCreateFormSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={4}>
              <FormControl required fullWidth size="small" varaint="outlined">
                <InputLabel id="demo-simple-select">Case Type</InputLabel>
                <Select
                  variant="outlined"
                  required
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  name="type"
                  value={data.type}
                  label="Case Type"
                  onChange={onCaseTypeChange}
                >
                  <MenuItem value="">Select</MenuItem>
                  {caseTypeList.map((t, i) => (
                    <MenuItem key={i} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <br />
          {data.type ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  value={data.title}
                  label="Title"
                  onChange={onInputChange}
                  size="small"
                  fullWidth
                  variant="outlined"
                  name="title"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6} lg={8}>
                <TextField
                  value={data.description}
                  label="Description"
                  onChange={onInputChange}
                  size="small"
                  fullWidth
                  variant="outlined"
                  name="description"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  value={data.claim_amount}
                  label="Amount claimed ($)"
                  onChange={onInputChange}
                  size="small"
                  fullWidth
                  variant="outlined"
                  name="claim_amount"
                  type="number"
                  inputProps={{ min: 0.01, step: "0.01" }}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  value={data.respondent_name}
                  label="Respondent name"
                  onChange={onInputChange}
                  size="small"
                  fullWidth
                  variant="outlined"
                  name="respondent_name"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  value={data.respondent_email}
                  label="Respondent email"
                  onChange={onInputChange}
                  size="small"
                  fullWidth
                  variant="outlined"
                  name="respondent_email"
                  type="email"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  value={data.respondent_phone}
                  label="Respondent phone"
                  onChange={onInputChange}
                  size="small"
                  fullWidth
                  variant="outlined"
                  name="respondent_phone"
                  type="tel"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Start Date"
                    required
                    // minDateTime={parseISO(new Date().toISOString())}
                    value={parseISO(data.start_date)}
                    onChange={(newValue) => {
                      onInputChange({
                        target: {
                          name: "start_date",
                          value: newValue.toISOString(),
                        },
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        required
                        {...params}
                        fullWidth
                        size="small"
                        variant="outlined"
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="End Date"
                    minDate={data.start_date}
                    value={parseISO(data.end_date)}
                    onChange={(newValue) => {
                      onInputChange({
                        target: {
                          name: "end_date",
                          value: newValue.toISOString(),
                        },
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        variant="outlined"
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <FormControl required fullWidth size="small" varaint="outlined">
                  <InputLabel id="demo-simple-select-s">Status</InputLabel>
                  <Select
                    variant="outlined"
                    required
                    labelId="demo-simple-select-label-s"
                    id="demo-simple-select-s"
                    name="status"
                    value={data.status}
                    label="Status"
                    onChange={onInputChange}
                  >
                    <MenuItem value="INITIAL">Initial</MenuItem>
                    <MenuItem value="OPEN">Open</MenuItem>
                    <MenuItem value="Closed">Closed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button variant="outlined" component="label" size="small">
                  Attach supporting documents (optional)
                  <input
                    hidden
                    multiple
                    type="file"
                    onChange={({ target }) => setFiles(Array.from(target.files || []))}
                  />
                </Button>
                {files.length ? (
                  <Typography className="mt-1 text-[13px] text-[#6b7a90]">
                    {files.map((f) => f.name).join(", ")}
                  </Typography>
                ) : null}
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="arbitration_agreement"
                      checked={data.arbitration_agreement}
                      onChange={onInputChange}
                      required
                    />
                  }
                  label="I confirm there is an agreement to arbitrate this dispute, or that the respondent has consented to it."
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <Box display="flex" columnGap={1} justifyContent="flex-end">
                  <Link to="/cases">
                    <Button
                      variant="outlined"
                      size="large"
                      color="secondary"
                      className="black-border-button"
                    >
                      Cancel
                    </Button>
                  </Link>

                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    size="large"
                    className="blue-button"
                    disabled={/^trial_user$/i.test(user.roles) || submitting}
                  >
                    Create
                  </Button>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <></>
          )}
        </Box>
      </Paper>

    </>
  );
}
